import {gql} from '@apollo/client';

export const GET_ALL_SUBSCRIPTION_PLANS = gql`
  query Data {
    getAllSubscriptionPlans {
      ... on ResponseWithDabaSubscriptionPlans {
        data {
          key
          name
          description
          isVisibleToUsers
          isEnabled
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const UPSERT_SUBSCRIPTION_PLAN = gql`
  mutation UpsertSubscriptionPlan($input: UpsertSubscriptionPlanInput!) {
    upsertSubscriptionPlan(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionPlan {
        message
      }
    }
  }
`;

export const GET_SUBSCRIPTION_PLANS_DETAILS = gql`
  query GetPlans($filter: SubscriptionFilterInput) {
    getAllSubscriptionPlans(filter: $filter) {
      ... on ResponseWithDabaSubscriptionPlans {
        message
        data {
          key
          name
          description
          isEnabled
          isVisibleToUsers
          reminderSettings {
            enabled
            maxShowCount
            dismissible
            autoCloseMs
            features {
              icon
              label
            }
            reminders {
              id
              enabled
              daysBeforeExpiry
              catalogTabs {
                id
                catalogKey
                isDefault
                appliedDiscount {
                  currency
                  type
                  value
                }
              }
              copyOverrides {
                headerBadgeText
                title
                subtitle
                footerText
                primaryCtaLabel
                secondaryCtaLabel
              }
            }
          }
          multiLang {
            language
            name
            description
            reminderSettings {
              features {
                label
              }
              reminders {
                reminderId
                copyOverrides {
                  headerBadgeText
                  title
                  subtitle
                  footerText
                  primaryCtaLabel
                  secondaryCtaLabel
                }
              }
            }
          }
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const UPSERT_SUBSCRIPTION_PLAN_DETAILS = gql`
  mutation UpsertPlan($input: UpsertSubscriptionPlanInput!) {
    upsertSubscriptionPlan(input: $input) {
      ... on ResponseWithSubscriptionPlan {
        message
        data {
          key
          name
          isEnabled
          isVisibleToUsers
          reminderSettings {
            enabled
            maxShowCount
            dismissible
            autoCloseMs
            features {
              icon
              label
            }
            reminders {
              id
              enabled
              daysBeforeExpiry
              catalogTabs {
                id
                catalogKey
                isDefault
                appliedDiscount {
                  currency
                  type
                  value
                }
              }
              copyOverrides {
                headerBadgeText
                title
                subtitle
                footerText
                primaryCtaLabel
                secondaryCtaLabel
              }
            }
          }
          multiLang {
            language
            name
            description
            reminderSettings {
              features {
                label
              }
              reminders {
                reminderId
                copyOverrides {
                  headerBadgeText
                  title
                  subtitle
                  footerText
                  primaryCtaLabel
                  secondaryCtaLabel
                }
              }
            }
          }
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const GET_PLAN_SUBSCRIPTIONS_CATALOGS = gql`
  query GetSubscriptionPlansCatalog($filter: SubscriptionFilterInput) {
    getSubscriptionPlansCatalog(filter: $filter) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionCatalog {
        data {
          key
          label
          description
          tag
          plan
          prices {
            currency
            amount
            discount {
              type
              value
            }
          }
          allowCoupon
          appleProductId
          purchaseType
          duration {
            unit
            value
          }
          redirectUrl
          allowRecurring
          defaultRecurring
          groupId
          isVisibleToUsers
          isEnabled
        }
      }
    }
  }
`;

export const UPSERT_SUBSCRIPTION_CATALOG_ITEM = gql`
  mutation UpsertSubscriptionCatalogItem(
    $input: DabaSubscriptionCatalogPriceInput!
  ) {
    upsertSubscriptionCatalogItem(input: $input) {
      ... on ResponseWithSubscriptionCatalogItem {
        message
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const UPSERT_SUBSCRIPTION_BUNDLE = gql`
  mutation UpsertSubscriptionBundle($input: UpsertSubscriptionBundleInput!) {
    upsertSubscriptionBundle(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionBundle {
        message
      }
    }
  }
`;

export const GET_SUBSCRIPTION_BUNDLES = gql`
  query Data {
    getDabaSubscriptionBundlesAdmin {
      ... on ResponseWithDabaSubscriptionBundles {
        data {
          key
          name
          description
          language
          multilang {
            language
            name
            description
          }
          isEnabled
          isVisibleToUsers
          items {
            catalogKey
            catalogItem {
              key
              label
              description
              tag
              plan
              prices {
                currency
                amount
              }
              purchaseType
              redirectUrl
              allowRecurring
              defaultRecurring
              groupId
              isVisibleToUsers
              isEnabled
            }
          }
          prices {
            currency
            amount
          }
        }
      }
      ... on Error {
        statusCode
        message
      }
    }
  }
`;

export const CHANGE_SUBSCRIPTION_PLAN_STATUS = gql`
  mutation ChangeSubscriptionPlanStatus(
    $input: SetSubscriptionResourceStatusInput!
  ) {
    changeSubscriptionPlanStatus(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionPlan {
        message
      }
    }
  }
`;

export const CHANGE_SUBSCRIPTION_PLAN_CATALOG_STATUS = gql`
  mutation ChangeSubscriptionCatalogItemStatus(
    $input: SetSubscriptionResourceStatusInput!
  ) {
    changeSubscriptionCatalogItemStatus(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionCatalogItem {
        message
      }
    }
  }
`;

export const CHANGE_SUBSCRIPTION_BUNDLE_STATUS = gql`
  mutation ChangeSubscriptionBundleStatus(
    $input: SetSubscriptionResourceStatusInput!
  ) {
    changeSubscriptionBundleStatus(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionBundle {
        message
      }
    }
  }
`;

export const MANUAL_SUBSCRIBE_USER_TO_SUBSCRIPTION = gql`
  mutation ManualSubscribeUserToSubscription(
    $input: ManualSubscribeUserToSubscriptionInput!
  ) {
    manualSubscribeUserToSubscription(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithManualSubscription {
        message
      }
    }
  }
`;

export const TOGGLE_SUBSCRIPTION_RECURRING_STATUS = gql`
  mutation ToggleSubscriptionRecurring(
    $input: ToggleSubscriptionRecurringInput!
  ) {
    toggleSubscriptionRecurring(input: $input) {
      ... on Error {
        statusCode
        message
      }
      ... on Response {
        message
      }
    }
  }
`;

export const DELETE_SUBSCRIPTION_BUNDLE = gql`
  mutation DeleteSubscriptionBundle($key: String!) {
    deleteSubscriptionBundle(key: $key) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithSubscriptionBundle {
        message
      }
    }
  }
`;
